/**
 * H5.5 — WordPress Deployment & Plugin
 *
 * Three tabs:
 *  - My Sites       — list connected WP sites with deploy / domain-bind / remove
 *  - New Site       — plan selector + subdomain form → provision via WP.com API
 *  - Plugin Connect — API key generation + connect existing WP site + install steps
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
    Globe,
    Plus,
    Trash2,
    CheckCircle,
    AlertCircle,
    Copy,
    Download,
    RefreshCw,
    ExternalLink,
    Key,
    Link2,
    Plug,
} from 'lucide-react';
import {
    GlassCard,
    GlassButton,
    GlassInput,
    GlassTabs,
    GlassBadge,
    ModuleHeader,
} from '../ui';
import {
    getWordPressPlans,
    createWordPressSite,
    getWordPressSites,
    deleteWordPressSite,
    deployWordPressWebsite,
    bindWordPressDomain,
    generateWordPressPluginKey,
    connectWordPressPlugin,
    downloadWordPressPlugin,
    downloadAcfSyncPlugin,
} from '../../services/api';
import PairingWizard from './PairingWizard';
import './WordPress.css';

/** Normalize FastAPI error detail — may be a string or a Pydantic validation error array */
const getApiError = (e: any, fallback: string): string => {
  const detail = e?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join('; ');
  }
  return fallback;
};

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface WPPlan {
    id: string;
    name: string;
    price_usd: number;
    description: string;
    features: string[];
}

interface WPSite {
    site_id: string;
    site_url: string;
    admin_url: string | null;
    plan: string | null;
    monthly_cost: number | null;
    custom_domain: string | null;
    status: string;
    connection_type: string;
    plugin_api_key_masked: string | null;
    created_at: string | null;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function statusVariant(status: string): 'success' | 'warning' | 'danger' | 'default' {
    if (status === 'active') return 'success';
    if (status === 'suspended') return 'warning';
    if (status === 'cancelled') return 'danger';
    return 'default';
}

function connectionVariant(type: string): 'info' | 'default' {
    return type === 'plugin' ? 'info' : 'default';
}

// ---------------------------------------------------------------------------
// My Sites tab
// ---------------------------------------------------------------------------

interface MySitesTabProps {
    sites: WPSite[];
    loading: boolean;
    onRefresh: () => void;
    onSwitchNewSite: () => void;
    onSwitchPlugin: () => void;
    onOpenPairingWizard: () => void;
}

const MySitesTab: React.FC<MySitesTabProps> = ({
    sites,
    loading,
    onRefresh,
    onSwitchNewSite,
    onSwitchPlugin,
    onOpenPairingWizard,
}) => {
    const [deployingId, setDeployingId] = useState<string | null>(null);
    const [deployResults, setDeployResults] = useState<Record<string, { pages: number; url: string }>>({});
    const [removingId, setRemovingId] = useState<string | null>(null);
    const [bindingId, setBindingId] = useState<string | null>(null);
    const [bindDomainValues, setBindDomainValues] = useState<Record<string, string>>({});
    const [bindSuccess, setBindSuccess] = useState<Record<string, boolean>>({});
    const [error, setError] = useState<string | null>(null);

    const handleDeploy = async (site: WPSite) => {
        setDeployingId(site.site_id);
        setError(null);
        try {
            const result = await deployWordPressWebsite(site.site_id);
            setDeployResults(prev => ({
                ...prev,
                [site.site_id]: { pages: result.pages_deployed, url: result.site_url },
            }));
        } catch (e: any) {
            setError(getApiError(e, 'Deploy failed.'));
        } finally {
            setDeployingId(null);
        }
    };

    const handleRemove = async (siteId: string) => {
        if (!window.confirm('Remove this WordPress site from AiMe? The site itself will not be deleted.')) return;
        setRemovingId(siteId);
        try {
            await deleteWordPressSite(siteId);
            onRefresh();
        } catch (e: any) {
            setError(getApiError(e, 'Remove failed.'));
        } finally {
            setRemovingId(null);
        }
    };

    const handleBindDomain = async (site: WPSite) => {
        const domain = bindDomainValues[site.site_id] || '';
        if (!domain.trim()) return;
        setBindingId(site.site_id);
        setError(null);
        try {
            await bindWordPressDomain(site.site_id, domain.trim());
            setBindSuccess(prev => ({ ...prev, [site.site_id]: true }));
            onRefresh();
        } catch (e: any) {
            setError(getApiError(e, 'Domain bind failed.'));
        } finally {
            setBindingId(null);
        }
    };

    if (loading) {
        return (
            <div className="wp-empty-state">
                <RefreshCw size={32} className="wp-empty-state-icon" />
                <p>Loading sites...</p>
            </div>
        );
    }

    if (sites.length === 0) {
        return (
            <div className="wp-empty-state">
                <Globe size={48} className="wp-empty-state-icon" />
                <h3>No WordPress sites connected</h3>
                <p>Provision a new WordPress.com site or connect an existing one via the AiMe plugin.</p>
                <div className="wp-empty-ctas">
                    <GlassButton variant="primary" onClick={onSwitchNewSite}>
                        <Plus size={16} />
                        New WordPress Site
                    </GlassButton>
                    <GlassButton variant="secondary" onClick={onOpenPairingWizard}>
                        <Link2 size={16} style={{ marginRight: 6 }} />
                        Connect Existing WordPress Site
                    </GlassButton>
                    <GlassButton variant="secondary" onClick={onSwitchPlugin}>
                        <Plug size={16} />
                        Connect via Plugin
                    </GlassButton>
                </div>
            </div>
        );
    }

    return (
        <div className="wp-sites-grid">
            {error && (
                <div className="wp-api-key-warning">
                    <AlertCircle size={16} />
                    {error}
                </div>
            )}
            {sites.map(site => (
                <GlassCard key={site.site_id} className="wp-site-card">
                    <div className="wp-site-header">
                        <a
                            href={site.site_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="wp-site-url"
                        >
                            {site.site_url}
                            <ExternalLink size={12} style={{ marginLeft: 4, verticalAlign: 'middle' }} />
                        </a>
                        <div className="wp-site-badges">
                            <GlassBadge variant={statusVariant(site.status)}>{site.status}</GlassBadge>
                            <GlassBadge variant={connectionVariant(site.connection_type)}>
                                {site.connection_type === 'plugin' ? 'Plugin' : 'WP.com'}
                            </GlassBadge>
                            {site.plan && (
                                <GlassBadge variant="default">
                                    {site.plan.charAt(0).toUpperCase() + site.plan.slice(1)}
                                </GlassBadge>
                            )}
                        </div>
                    </div>

                    <div className="wp-site-meta">
                        {site.monthly_cost != null && (
                            <span>${site.monthly_cost.toFixed(2)}/mo &nbsp;&bull;&nbsp;</span>
                        )}
                        {site.custom_domain ? (
                            <span>Domain: {site.custom_domain} &nbsp;&bull;&nbsp;</span>
                        ) : null}
                        {site.created_at && (
                            <span>Added {new Date(site.created_at).toLocaleDateString()}</span>
                        )}
                    </div>

                    <div className="wp-site-actions">
                        <GlassButton
                            variant="primary"
                            onClick={() => handleDeploy(site)}
                            disabled={deployingId === site.site_id}
                        >
                            {deployingId === site.site_id ? (
                                <RefreshCw size={15} style={{ animation: 'spin 1s linear infinite' }} />
                            ) : (
                                <Globe size={15} />
                            )}
                            Deploy Website
                        </GlassButton>

                        {site.admin_url && site.admin_url !== 'pending/wp-admin' && (
                            <GlassButton
                                variant="secondary"
                                onClick={() => window.open(site.admin_url!, '_blank')}
                            >
                                <ExternalLink size={15} />
                                WP Admin
                            </GlassButton>
                        )}

                        <GlassButton
                            variant="danger"
                            onClick={() => handleRemove(site.site_id)}
                            disabled={removingId === site.site_id}
                        >
                            <Trash2 size={15} />
                            Remove
                        </GlassButton>
                    </div>

                    {/* Deploy result */}
                    {deployResults[site.site_id] && (
                        <div className="wp-success-banner">
                            <p>
                                <CheckCircle
                                    size={14}
                                    style={{ verticalAlign: 'middle', marginRight: 6, color: 'var(--success, #10b981)' }}
                                />
                                Deployed {deployResults[site.site_id].pages} page(s) to{' '}
                                <a href={deployResults[site.site_id].url} target="_blank" rel="noopener noreferrer">
                                    {deployResults[site.site_id].url}
                                </a>
                            </p>
                        </div>
                    )}

                    {/* Bind domain row */}
                    <div className="wp-domain-bind-row">
                        <GlassInput
                            value={bindDomainValues[site.site_id] || ''}
                            onChange={(val) =>
                                setBindDomainValues(prev => ({ ...prev, [site.site_id]: val }))
                            }
                            placeholder="yourdomain.com"
                        />
                        <GlassButton
                            variant="secondary"
                            onClick={() => handleBindDomain(site)}
                            disabled={bindingId === site.site_id}
                        >
                            <Link2 size={15} />
                            {bindSuccess[site.site_id] ? 'Domain saved' : 'Bind Domain'}
                        </GlassButton>
                    </div>

                    {/* Resume connection for pending plugin sites */}
                    {site.connection_type === 'plugin' && site.status === 'pending' && (
                        <GlassButton
                            variant="secondary"
                            onClick={(e) => { e.stopPropagation(); onOpenPairingWizard(); }}
                            style={{ marginTop: 8 }}
                        >
                            Resume Connection
                        </GlassButton>
                    )}
                </GlassCard>
            ))}
        </div>
    );
};

// ---------------------------------------------------------------------------
// New Site tab
// ---------------------------------------------------------------------------

interface NewSiteTabProps {
    plans: WPPlan[];
    plansLoading: boolean;
    onSiteCreated: () => void;
}

const NewSiteTab: React.FC<NewSiteTabProps> = ({ plans, plansLoading, onSiteCreated }) => {
    const [selectedPlan, setSelectedPlan] = useState<string>('starter');
    const [subdomain, setSubdomain] = useState('');
    const [siteTitle, setSiteTitle] = useState('');
    const [creating, setCreating] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [created, setCreated] = useState<{ siteUrl: string; adminUrl: string; stubMode: boolean } | null>(null);

    const handleCreate = async () => {
        if (!subdomain.trim()) {
            setError('Subdomain is required.');
            return;
        }
        setCreating(true);
        setError(null);
        try {
            const result = await createWordPressSite({
                subdomain: subdomain.trim().toLowerCase().replace(/\s+/g, '-'),
                plan: selectedPlan,
                title: siteTitle.trim() || undefined,
            });
            setCreated({
                siteUrl: result.site_url,
                adminUrl: result.admin_url,
                stubMode: result.stub_mode ?? true,
            });
            onSiteCreated();
        } catch (e: any) {
            setError(getApiError(e, 'Failed to create site.'));
        } finally {
            setCreating(false);
        }
    };

    return (
        <div>
            {error && (
                <div className="wp-api-key-warning">
                    <AlertCircle size={16} />
                    {error}
                </div>
            )}

            {created ? (
                <div className="wp-success-banner">
                    <p>
                        <CheckCircle
                            size={14}
                            style={{ verticalAlign: 'middle', marginRight: 6, color: 'var(--success, #10b981)' }}
                        />
                        Site created:{' '}
                        <a href={created.siteUrl} target="_blank" rel="noopener noreferrer">
                            {created.siteUrl}
                        </a>
                    </p>
                    {created.adminUrl && created.adminUrl !== 'pending/wp-admin' && (
                        <p>
                            WP Admin:{' '}
                            <a href={created.adminUrl} target="_blank" rel="noopener noreferrer">
                                {created.adminUrl}
                            </a>
                        </p>
                    )}
                    {created.stubMode && (
                        <p className="wp-stub-notice">
                            Running in stub mode — configure WORDPRESS_COM_ACCESS_TOKEN for live provisioning.
                        </p>
                    )}
                    <div style={{ marginTop: 14 }}>
                        <GlassButton variant="secondary" onClick={() => setCreated(null)}>
                            <Plus size={15} />
                            Create Another Site
                        </GlassButton>
                    </div>
                </div>
            ) : (
                <>
                    {/* Plan selector */}
                    <div className="wp-section">
                        <h3>Select Plan</h3>
                        <p className="wp-pricing-note">
                            Pricing dynamically reflects WordPress.com rates. Billed directly through WordPress.com.
                        </p>
                        {plansLoading ? (
                            <p style={{ color: 'var(--text-secondary)', fontSize: '0.88rem' }}>Loading plans...</p>
                        ) : (
                            <div className="wp-plans-grid">
                                {plans.map(plan => (
                                    <div
                                        key={plan.id}
                                        className={`wp-plan-card${selectedPlan === plan.id ? ' selected' : ''}`}
                                        onClick={() => setSelectedPlan(plan.id)}
                                    >
                                        <div className="wp-plan-header">
                                            <span className="wp-plan-name">{plan.name}</span>
                                            <span className="wp-plan-price">
                                                ${plan.price_usd.toFixed(0)}
                                                <span className="wp-plan-price-sub">/mo</span>
                                            </span>
                                        </div>
                                        <p className="wp-plan-desc">{plan.description}</p>
                                        {plan.features && plan.features.length > 0 && (
                                            <ul className="wp-plan-features">
                                                {plan.features.map((f, i) => (
                                                    <li key={i}>{f}</li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <hr className="wp-section-divider" />

                    {/* Site details */}
                    <div className="wp-section">
                        <h3>Site Details</h3>
                        <GlassInput
                            label="Subdomain"
                            value={subdomain}
                            onChange={setSubdomain}
                            placeholder="mybusiness (→ mybusiness.wordpress.com)"
                        />
                        <div style={{ marginBottom: 16 }} />
                        <GlassInput
                            label="Site Title (optional)"
                            value={siteTitle}
                            onChange={setSiteTitle}
                            placeholder="My Business Website"
                        />
                        <div style={{ marginTop: 20 }}>
                            <GlassButton
                                variant="primary"
                                onClick={handleCreate}
                                disabled={creating || !subdomain.trim()}
                            >
                                {creating ? (
                                    <RefreshCw size={15} style={{ animation: 'spin 1s linear infinite' }} />
                                ) : (
                                    <Plus size={15} />
                                )}
                                {creating ? 'Creating...' : 'Create Site'}
                            </GlassButton>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

// ---------------------------------------------------------------------------
// Plugin Connect tab
// ---------------------------------------------------------------------------

interface PluginConnectTabProps {
    onSiteConnected: () => void;
}

const PluginConnectTab: React.FC<PluginConnectTabProps> = ({ onSiteConnected }) => {
    const [showInlinePairing, setShowInlinePairing] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [generatedKey, setGeneratedKey] = useState<string | null>(null);
    const [copied, setCopied] = useState(false);
    const [connectUrl, setConnectUrl] = useState('');
    const [connectKey, setConnectKey] = useState('');
    const [connecting, setConnecting] = useState(false);
    const [connectResult, setConnectResult] = useState<{ siteUrl: string } | null>(null);
    const [error, setError] = useState<string | null>(null);

    const handleGenerateKey = async () => {
        setGenerating(true);
        setError(null);
        try {
            const result = await generateWordPressPluginKey();
            setGeneratedKey(result.api_key);
        } catch (e: any) {
            setError(getApiError(e, 'Failed to generate key.'));
        } finally {
            setGenerating(false);
        }
    };

    const handleCopyKey = () => {
        if (generatedKey) {
            navigator.clipboard.writeText(generatedKey).then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            });
        }
    };

    const handleConnect = async () => {
        if (!connectUrl.trim() || !connectKey.trim()) {
            setError('Site URL and API key are required.');
            return;
        }
        setConnecting(true);
        setError(null);
        try {
            const result = await connectWordPressPlugin({ site_url: connectUrl.trim(), api_key: connectKey.trim() });
            setConnectResult({ siteUrl: result.site_url });
        } catch (e: any) {
            setError(getApiError(e, 'Connection failed. Check the API key and try again.'));
        } finally {
            setConnecting(false);
        }
    };

    const [downloadingPlugin, setDownloadingPlugin] = useState(false);

    const handleDownloadPlugin = async () => {
        setDownloadingPlugin(true);
        setError(null);
        try {
            const blob = await downloadWordPressPlugin();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'aime-connect.zip';
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (e: any) {
            setError(getApiError(e, 'Failed to download the plugin. Please try again.'));
        } finally {
            setDownloadingPlugin(false);
        }
    };

    const [downloadingAcfSync, setDownloadingAcfSync] = useState(false);

    const handleDownloadAcfSyncPlugin = async () => {
        setDownloadingAcfSync(true);
        setError(null);
        try {
            const blob = await downloadAcfSyncPlugin();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'aime-acf-sync.zip';
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (e: any) {
            setError(getApiError(e, 'Failed to download the plugin. Please try again.'));
        } finally {
            setDownloadingAcfSync(false);
        }
    };

    return (
        <div>
            {error && (
                <div className="wp-api-key-warning">
                    <AlertCircle size={16} />
                    {error}
                </div>
            )}

            {/* Pairing wizard — inline connect with code */}
            <div className="wp-section">
                {!showInlinePairing ? (
                    <GlassButton variant="primary" onClick={() => setShowInlinePairing(true)} style={{ marginBottom: 16 }}>
                        <Link2 size={15} style={{ marginRight: 6 }} />
                        Connect with Pairing Code
                    </GlassButton>
                ) : (
                    <div style={{ marginBottom: 24 }}>
                        <PairingWizard
                            embedded={true}
                            onConnected={(site) => {
                                setShowInlinePairing(false);
                                onSiteConnected();
                            }}
                            onCancel={() => setShowInlinePairing(false)}
                        />
                    </div>
                )}
            </div>

            <hr className="wp-section-divider" />

            {/* Section 1: Generate API key */}
            <div className="wp-section">
                <h3>
                    <Key size={16} />
                    Step 1 — Generate API Key
                </h3>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.88rem', marginBottom: 14 }}>
                    Generate a key to paste into the AiMe Connect plugin on your WordPress site.
                </p>

                {generatedKey ? (
                    <>
                        <div className="wp-api-key-warning">
                            <AlertCircle size={15} />
                            Copy this key now — it will not be shown again.
                        </div>
                        <div className="wp-api-key-box">
                            <span className="wp-api-key-value">{generatedKey}</span>
                            <GlassButton variant="secondary" onClick={handleCopyKey}>
                                <Copy size={15} />
                                {copied ? 'Copied!' : 'Copy'}
                            </GlassButton>
                        </div>
                    </>
                ) : (
                    <GlassButton
                        variant="primary"
                        onClick={handleGenerateKey}
                        disabled={generating}
                    >
                        {generating ? (
                            <RefreshCw size={15} style={{ animation: 'spin 1s linear infinite' }} />
                        ) : (
                            <Key size={15} />
                        )}
                        Generate New API Key
                    </GlassButton>
                )}
            </div>

            <hr className="wp-section-divider" />

            {/* Section 2: Connect existing site */}
            <div className="wp-section">
                <h3>
                    <Plug size={16} />
                    Step 2 — Connect Your WordPress Site
                </h3>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.88rem', marginBottom: 14 }}>
                    After installing the AiMe Connect plugin and saving your API key there, enter your
                    site URL and the same key below to complete the connection.
                </p>

                {connectResult ? (
                    <div className="wp-success-banner">
                        <p>
                            <CheckCircle
                                size={14}
                                style={{ verticalAlign: 'middle', marginRight: 6, color: 'var(--success, #10b981)' }}
                            />
                            Connected: {connectResult.siteUrl}
                        </p>
                    </div>
                ) : (
                    <>
                        <GlassInput
                            label="WordPress Site URL"
                            value={connectUrl}
                            onChange={setConnectUrl}
                            placeholder="https://yoursite.com"
                        />
                        <div style={{ marginBottom: 16 }} />
                        <GlassInput
                            label="API Key"
                            value={connectKey}
                            onChange={setConnectKey}
                            placeholder="aime_wpk_..."
                        />
                        <div style={{ marginTop: 16 }}>
                            <GlassButton
                                variant="primary"
                                onClick={handleConnect}
                                disabled={connecting}
                            >
                                {connecting ? (
                                    <RefreshCw size={15} style={{ animation: 'spin 1s linear infinite' }} />
                                ) : (
                                    <Plug size={15} />
                                )}
                                {connecting ? 'Connecting...' : 'Connect Site'}
                            </GlassButton>
                        </div>
                    </>
                )}
            </div>

            <hr className="wp-section-divider" />

            {/* Section 3: Installation instructions */}
            <div className="wp-section">
                <h3>
                    <Download size={16} />
                    Plugin Installation Instructions
                </h3>
                <ol className="wp-install-steps">
                    <li>
                        Download the AiMe Connect plugin file using the button below and save it to
                        your computer.
                    </li>
                    <li>
                        In your WordPress admin, go to <strong>Plugins &rarr; Add New &rarr; Upload Plugin</strong>.
                        Select the downloaded <code>aime-connect.zip</code> file and click{' '}
                        <strong>Install Now</strong>, then <strong>Activate</strong>.
                    </li>
                    <li>
                        Go to <strong>Settings &rarr; AiMe Connect</strong> in your WordPress admin.
                        Paste the API key you generated above and click <strong>Save API Key</strong>.
                    </li>
                    <li>
                        Return here and complete Step 2 above to finish the connection.
                    </li>
                </ol>
                <div style={{ marginTop: 16 }}>
                    <GlassButton
                        variant="secondary"
                        onClick={handleDownloadPlugin}
                        disabled={downloadingPlugin}
                    >
                        <Download size={15} />
                        {downloadingPlugin ? 'Downloading…' : 'Download aime-connect.zip'}
                    </GlassButton>
                </div>
            </div>

            <hr className="wp-section-divider" />

            {/* Section 4: Optional ACF content sync companion plugin */}
            <div className="wp-section">
                <h3>
                    <Download size={16} />
                    Optional: ACF Content Sync Plugin
                </h3>
                <p style={{ maxWidth: 640 }}>
                    Most WordPress themes don't need this — skip it unless your site's theme or
                    page builder (e.g. <strong>YOOtheme Pro</strong>) is configured to display
                    article content from an Advanced Custom Fields field instead of WordPress's
                    native content field. If posts publish successfully from AiMe but the body
                    text doesn't appear on the live page, this is almost always why.
                </p>
                <ol className="wp-install-steps">
                    <li>
                        Confirm ACF (free or Pro) is already installed and active on the site.
                    </li>
                    <li>
                        Download and install <code>aime-acf-sync.zip</code> the same way as the
                        AiMe Connect plugin above (<strong>Plugins &rarr; Add New &rarr; Upload
                        Plugin</strong>, then <strong>Activate</strong>).
                    </li>
                    <li>
                        Go to <strong>Settings &rarr; AIME ACF Sync</strong> in WordPress admin,
                        click <strong>Scan ACF Fields</strong> to find the field your theme reads
                        from, select it, then <strong>Test Connection</strong> and save.
                    </li>
                </ol>
                <div style={{ marginTop: 16 }}>
                    <GlassButton
                        variant="secondary"
                        onClick={handleDownloadAcfSyncPlugin}
                        disabled={downloadingAcfSync}
                    >
                        <Download size={15} />
                        {downloadingAcfSync ? 'Downloading…' : 'Download aime-acf-sync.zip'}
                    </GlassButton>
                </div>
            </div>
        </div>
    );
};

// ---------------------------------------------------------------------------
// WordPressModule — root component
// ---------------------------------------------------------------------------

const TABS = [
    { id: 'my-sites', label: 'My Sites' },
    { id: 'new-site', label: 'New Site' },
    { id: 'plugin', label: 'Plugin Connect' },
];

const WordPressModule: React.FC = () => {
    const [activeTab, setActiveTab] = useState('my-sites');
    const [sites, setSites] = useState<WPSite[]>([]);
    const [plans, setPlans] = useState<WPPlan[]>([]);
    const [sitesLoading, setSitesLoading] = useState(true);
    const [plansLoading, setPlansLoading] = useState(true);
    const [showPairingWizard, setShowPairingWizard] = useState(false);

    const loadSites = useCallback(async () => {
        setSitesLoading(true);
        try {
            const data = await getWordPressSites();
            setSites(data.sites || []);
        } catch {
            setSites([]);
        } finally {
            setSitesLoading(false);
        }
    }, []);

    const loadPlans = useCallback(async () => {
        setPlansLoading(true);
        try {
            const data = await getWordPressPlans();
            setPlans(data.plans || []);
        } catch {
            setPlans([]);
        } finally {
            setPlansLoading(false);
        }
    }, []);

    useEffect(() => {
        loadSites();
        loadPlans();
    }, [loadSites, loadPlans]);

    const handleSiteCreated = () => {
        loadSites();
        setActiveTab('my-sites');
    };

    return (
        <div className="wordpress-module">
            <ModuleHeader
                title="WordPress"
                breadcrumb={['WordPress']}
                actions={[
                    <GlassButton key="connect-existing" variant="secondary" onClick={() => setShowPairingWizard(true)}>
                        <Link2 size={16} style={{ marginRight: 6 }} />
                        Connect Existing WordPress Site
                    </GlassButton>,
                    <GlassButton key="refresh" variant="secondary" onClick={loadSites}>
                        <RefreshCw size={15} />
                        Refresh
                    </GlassButton>,
                ]}
            />

            <GlassTabs
                tabs={TABS}
                activeTab={activeTab}
                onChange={setActiveTab}
            />

            <div style={{ marginTop: 24 }}>
                {activeTab === 'my-sites' && (
                    <MySitesTab
                        sites={sites}
                        loading={sitesLoading}
                        onRefresh={loadSites}
                        onSwitchNewSite={() => setActiveTab('new-site')}
                        onSwitchPlugin={() => setActiveTab('plugin')}
                        onOpenPairingWizard={() => setShowPairingWizard(true)}
                    />
                )}

                {activeTab === 'new-site' && (
                    <NewSiteTab
                        plans={plans}
                        plansLoading={plansLoading}
                        onSiteCreated={handleSiteCreated}
                    />
                )}

                {activeTab === 'plugin' && (
                    <PluginConnectTab
                        onSiteConnected={() => {
                            loadSites();
                            setActiveTab('my-sites');
                        }}
                    />
                )}
            </div>

            {showPairingWizard && (
                <PairingWizard
                    embedded={false}
                    onConnected={(site) => {
                        setShowPairingWizard(false);
                        loadSites();
                    }}
                    onCancel={() => setShowPairingWizard(false)}
                />
            )}
        </div>
    );
};

export default WordPressModule;
